/**
 * `wayai query-relationships …` — traversal from one record to its related
 * records. A namespace with no subcommands: the traversal IS the command
 * (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { Command } from 'commander';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, pathSegment, requireBase } from '../helpers.js';

/**
 * The user-facing direction words, in terms of the record you named. The server
 * speaks of the EDGE's endpoints instead, so `outgoing` (edges leaving your
 * record) is the edge's `source` side and `incoming` is its `target` side.
 * Anything unrecognized is forwarded verbatim for the server to reject.
 */
const DIRECTIONS: Record<string, string> = {
  outgoing: 'source',
  incoming: 'target',
  both: 'both',
};

export function buildQueryRelationshipsCommand(): Command {
  return new Command('query-relationships')
    .description('Query related records')
    .argument('<record_type>', 'RecordType of the source record')
    .argument('<id>', 'Source record ID')
    .option('--type <type>', 'Filter by relationship type')
    .option('--direction <dir>', 'Direction: outgoing, incoming, or both', 'both')
    .option('--limit <n>', 'Max results', '50')
    .option('--offset <n>', 'Skip results', '0')
    .action(async function (
      this: Command,
      recordType: string,
      id: string,
      opts: { type?: string; direction: string; limit: string; offset: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const params = new URLSearchParams();
      if (opts.type) params.set('rel_type', opts.type);
      params.set('direction', DIRECTIONS[opts.direction] ?? opts.direction);
      params.set('limit', opts.limit);
      params.set('offset', opts.offset);
      printOutput(
        await client.request(
          'GET',
          `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${pathSegment(id, 'record id')}/related?${params.toString()}`,
        ),
        outputFormat(this),
      );
    });
}
